import api from './api';

export type SlotStatus = 'AVAILABLE' | 'BOOKED' | 'BLOCKED_TRAVEL' | 'SYSTEM_LOCKED' | 'BLOCKED_PRIVE';
export type LocatieType = 'ONLINE' | 'WERKGEVER' | 'EXTERN';
export type AfspraakStatus = 'PENDING_APPROVAL' | 'TEGENVOORSTEL' | 'SCHEDULED' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';
export type SessieType = 'intake' | 'followup';

export interface ProfessionalSlot {
  id: string;
  start_tijd: string;
  eind_tijd: string;
  status: SlotStatus;
  dossier_id: string | null;
  afspraak_id: string | null;
}

export interface AgendaAfspraak {
  id: string;
  dossier_id: string;
  start_tijd: string;
  eind_tijd: string;
  sessie_type: SessieType;
  sessie_nummer: number;
  locatie_type: LocatieType;
  locatie_omschrijving: string | null;
  status: AfspraakStatus;
  geboekt_door: 'MEDEWERKER' | 'PROFESSIONAL';
  reistijd_zone: number | null;
  reistijd_uren: string | number | null;
  client_registration_number: string | null;
  professional_type: string | null;
  conversation_count: number | null;
}

export interface AgendaDossier {
  id: string;
  client_registration_number: string | null;
  professional_type: string | null;
  status: string;
  sessies_verbruikt: number;
  volgende_sessie_type: SessieType;
  volgende_sessie_nummer: number;
  duur_minuten: number;
  max_sessies: number | null;
  plafond_bereikt: boolean;
}

export interface BeschikbareStart {
  start_tijd: string;
  eind_tijd: string;
  slot_ids: string[];
  duur_minuten: number;
}

export interface PortaalContext {
  professional_naam: string | null;
  professional_type: string | null;
  sessie_type: SessieType;
  sessie_nummer: number;
  duur_minuten: number;
  plafond_bereikt: boolean;
  max_sessies: number | null;
  /** Werkwijze-grendel: professional werkt 100% online (BLAUW) */
  alleen_online?: boolean;
  /** Openstaand verzoek (wacht op de professional) of tegenvoorstel (wacht op de medewerker). */
  open_afspraak: {
    id: string;
    status: 'PENDING_APPROVAL' | 'TEGENVOORSTEL';
    start_tijd: string;
    eind_tijd: string;
    locatie_type: LocatieType;
    locatie_omschrijving: string | null;
    aangevraagd_start_tijd: string | null;
  } | null;
}

export type PoolStatus = 'GROEN' | 'BLAUW' | 'ORANJE' | 'ROOD';

export interface PoolAlternatief {
  professional_id: string;
  naam: string;
  pool_status: PoolStatus;
}

// ── Professional Cockpit ────────────────────────────────────────────────────

export async function openSlots(blokken: { start: string; end: string }[]) {
  const { data } = await api.post('/agenda/professional/slots', { blokken });
  return data.data as { aangemaakt: number; grid_minuten: number };
}

export async function getSlots(vanaf?: string, tot?: string) {
  const { data } = await api.get('/agenda/professional/slots', { params: { vanaf, tot } });
  return data.data as ProfessionalSlot[];
}

export async function sluitSlot(slotId: string) {
  await api.delete(`/agenda/professional/slots/${slotId}`);
}

export async function getAfspraken(vanaf?: string, tot?: string) {
  const { data } = await api.get('/agenda/professional/afspraken', { params: { vanaf, tot } });
  return data.data as AgendaAfspraak[];
}

export async function getAgendaDossiers() {
  const { data } = await api.get('/agenda/professional/dossiers');
  return data.data as AgendaDossier[];
}

export async function getDossierSlots(dossierId: string) {
  const { data } = await api.get(`/agenda/professional/dossiers/${dossierId}/beschikbare-slots`);
  return data.data as {
    sessie_type: SessieType;
    sessie_nummer: number;
    duur_minuten: number;
    plafond_bereikt: boolean;
    slots: BeschikbareStart[];
  };
}

export async function planVervolgsessie(payload: {
  dossier_id: string;
  start_tijd: string;
  locatie_type: LocatieType;
  locatie_omschrijving?: string | null;
}) {
  const { data } = await api.post('/agenda/professional/afspraken', payload);
  return data.data;
}

export async function registreerVoltooid(afspraakId: string) {
  const { data } = await api.post(`/agenda/professional/afspraken/${afspraakId}/voltooid`);
  return data.data as {
    sessies_verbruikt: number;
    max_sessies: number | null;
    verlenging_nodig: boolean;
  };
}

export async function registreerNoShow(afspraakId: string) {
  const { data } = await api.post(`/agenda/professional/afspraken/${afspraakId}/no-show`);
  return data.data as { aantal_uren: number; uurtarief: number };
}

export async function annuleerAfspraak(afspraakId: string, reden?: string) {
  await api.post(`/agenda/professional/afspraken/${afspraakId}/annuleren`, { reden });
}

export async function getBoekingsLink(dossierId: string) {
  const { data } = await api.post(`/agenda/professional/dossiers/${dossierId}/boekingslink`);
  return data.data as { token: string; expires_at: string };
}

// ── Medewerkerportaal (anoniem) ─────────────────────────────────────────────

export async function getPortaalContext(token: string) {
  const { data } = await api.get(`/agenda/portaal/${token}`);
  return data.data as PortaalContext;
}

export async function getPortaalSlots(token: string) {
  const { data } = await api.get(`/agenda/portaal/${token}/beschikbare-slots`);
  return data.data as { sessie_type: SessieType; duur_minuten: number; slots: BeschikbareStart[] };
}

export async function boekPortaalAfspraak(
  token: string,
  payload: { start_tijd: string; locatie_type: LocatieType; locatie_omschrijving?: string | null }
) {
  const { data } = await api.post(`/agenda/portaal/${token}/boeken`, payload);
  return data.data as {
    status: 'PENDING_APPROVAL' | 'SCHEDULED';
    start_tijd: string;
    eind_tijd: string;
    duur_minuten: number;
    sessie_type: SessieType;
  };
}

export async function beantwoordPortaalTegenvoorstel(token: string, afspraakId: string, akkoord: boolean) {
  const { data } = await api.post(`/agenda/portaal/${token}/afspraken/${afspraakId}/tegenvoorstel`, { akkoord });
  return data.data as { afspraak_id: string; status: 'SCHEDULED' | 'CANCELLED' };
}

export async function getPoolAlternatieven(token: string) {
  const { data } = await api.get(`/agenda/portaal/${token}/pool-alternatieven`);
  return data.data as { wisselbaar: boolean; alternatieven: PoolAlternatief[] };
}

export async function kiesPoolProfessional(token: string, professionalId: string) {
  const { data } = await api.post(`/agenda/portaal/${token}/kies-professional`, {
    professional_id: professionalId,
  });
  return data.data as { professional_naam: string };
}

// ── Privé-agendablokkades (migratie 188) ────────────────────────────────────

export interface AgendaBlokkade {
  id: string;
  start_tijd: string;
  eind_tijd: string;
  omschrijving: string | null;
}

export async function getBlokkades() {
  const { data } = await api.get('/agenda/professional/blokkades');
  return data.data as AgendaBlokkade[];
}

export async function maakBlokkade(payload: { start_tijd: string; eind_tijd: string; omschrijving?: string | null }) {
  const { data } = await api.post('/agenda/professional/blokkades', payload);
  return data.data as { blokkade: AgendaBlokkade; geblokkeerde_slots: number; conflicten: number };
}

export async function verwijderBlokkade(id: string) {
  await api.delete(`/agenda/professional/blokkades/${id}`);
}

// ── Agenda Matrix Center (migratie 192) ─────────────────────────────────────

export type MatrixKleur = 'mint' | 'blauw' | 'goud' | 'violet' | 'terracotta' | 'mos' | 'zand' | 'paars' | 'reistijd';
export type MatrixBron =
  | 'beschikbaar' | 'medewerker' | 'b2b' | 'kennismaking' | 'intervisie' | 'academie' | 'event' | 'blokkade' | 'reistijd';
export type BlokkadeSoort = 'PRIVE' | 'NETWERK' | 'VAKANTIE' | 'ZIEK';

export interface MatrixItem {
  id: string;
  bron: MatrixBron;
  kleur: MatrixKleur;
  ref_id: string;
  start: string;
  eind: string;
  hele_dag: boolean;
  titel: string;
  ondertitel: string | null;
  status: string | null;
  wacht_op_bevestiging: boolean;
  locatie_type: string | null;
  locatie: string | null;
  video_url: string | null;
  deelnemen_vanaf: string | null;
  link: string | null;
  meta: Record<string, any>;
}

export async function getMatrix(vanaf: string, tot: string) {
  const { data } = await api.get('/agenda/professional/matrix', { params: { vanaf, tot } });
  return data.data as MatrixItem[];
}

export async function planMatrixSessie(payload: {
  dossier_id: string;
  start_tijd: string;
  locatie_type: LocatieType;
  locatie_omschrijving?: string | null;
}) {
  const { data } = await api.post('/agenda/professional/matrix/sessie', payload);
  return data.data as { afspraak_id: string; start_tijd: string; eind_tijd: string; status: string };
}

export async function maakSoortBlokkade(payload: { start_tijd: string; eind_tijd: string; omschrijving?: string | null; soort: BlokkadeSoort }) {
  const { data } = await api.post('/agenda/professional/blokkades', payload);
  return data.data as { blokkade: AgendaBlokkade; geblokkeerde_slots: number; conflicten: number };
}

export interface CalamiteitOverzicht {
  tot: string;
  medewerker_afspraken: number;
  b2b_afspraken: number;
}

export async function getCalamiteitVoorvertoning() {
  const { data } = await api.get('/agenda/professional/calamiteit');
  return data.data as CalamiteitOverzicht;
}

export async function meldCalamiteit(omschrijving?: string | null) {
  const { data } = await api.post('/agenda/professional/calamiteit', { omschrijving: omschrijving || null });
  return data.data as CalamiteitOverzicht;
}

export interface Contactpersoon {
  user_id: string;
  naam: string;
  rol: string;
  afdeling: string | null;
}

export async function getContactpersonen(employerId: string) {
  const { data } = await api.get(`/agenda/professional/werkgevers/${employerId}/contactpersonen`);
  return data.data as Contactpersoon[];
}

export async function nodigExternUit(payload: {
  employer_id: string;
  external_email: string;
  external_telefoon?: string | null;
  soort: string;
  titel: string;
  omschrijving?: string | null;
  start_tijd: string;
  eind_tijd: string;
  locatie_type: string;
}) {
  const { data } = await api.post('/agenda/professional/externe-uitnodigingen', payload);
  return data.data as { b2b_appointment_id: string; verzonden: { email: boolean; sms: boolean } };
}

export interface ExterneUitnodiging {
  titel: string;
  omschrijving: string | null;
  start_tijd: string;
  eind_tijd: string;
  locatie: string;
  professional: string;
  organisatie: string;
  status: 'UITGENODIGD' | 'BEVESTIGD' | 'GEWEIGERD' | 'VERLOPEN' | 'VERVALLEN';
}

export async function getUitnodiging(token: string) {
  const { data } = await api.get(`/agenda/uitnodiging/${token}`);
  return data.data as ExterneUitnodiging;
}

export async function beantwoordUitnodiging(token: string, akkoord: boolean) {
  const { data } = await api.post(`/agenda/uitnodiging/${token}/antwoord`, { akkoord });
  return data.data as { status: 'BEVESTIGD' | 'GEWEIGERD' };
}
